/**
 * Read-only snapshot of the layout the Fab Assistant receives with every chat
 * turn, so it can answer questions about counts, validation, OHTs, KPIs and
 * throughput directly instead of only routing intents.
 *
 * Kept deliberately small: summaries and a capped OHT roster, never element
 * lists. The backend forwards it to the model as part of the canvas context.
 */
import type { LayoutBomResult } from '../../utils/bom';
import type { ValidationState } from '../../types';
import type { AmhsLiveReportPayload } from '../../types/amhsStream';

/** Most OHT rows the roster carries; the rest is summarised by the counts above it. */
export const ASSISTANT_MAX_OHT_ROWS = 40;

export type AssistantSimulationInput = {
  source: 'legacy' | 'live';
  running: boolean;
  simulationTime: number;
  live: {
    connectionStatus: string;
    dispatchPolicy?: string | null;
    simSpeed?: number | null;
    liveReport?: AmhsLiveReportPayload | null;
  };
  lastLiveReport?: AmhsLiveReportPayload | null;
};

/** The imported DXF floor plan, as the floorplan slice holds it. */
export type AssistantFloorPlanInput = {
  name: string | null;
  floorPlanId: string | null;
  bounds: { minX: number; minY: number; maxX: number; maxY: number } | null;
  dxfUnit: string;
  dxfUnitsPerMeter: number;
  /** Metres per canvas unit, from the same calibration the BOM uses. */
  metersPerWorldUnit: number;
  polylineCount: number;
  labelCount: number;
  layers: ReadonlyArray<{ name: string; visible: boolean; category?: string }>;
  visible: boolean;
  /** Facility zones detected on the plan, if any. */
  regions: ReadonlyArray<{ type: string; name: string }>;
};

/** Equipment overlays extracted from the DXF (tools, stockers) and their review state. */
export type AssistantEquipmentInput = {
  instances: ReadonlyArray<{
    name: string;
    objectType: string;
    category: string;
    equipmentType?: string;
    reviewStatus?: string;
  }>;
};

/** Most DXF layer names listed; the counts cover the rest. */
export const ASSISTANT_MAX_LAYER_NAMES = 60;

export type AssistantOhtRow = {
  ohtId: string;
  fleet?: string;
  requests?: number;
  busySec?: number;
  utilization?: number;
  pathLengthM?: number;
};

export type AssistantLayoutSummary = {
  /** The imported CAD drawing. `null` when no floor plan is open. */
  floorPlan: {
    name: string | null;
    loaded: boolean;
    visible: boolean;
    /** Drawing extents converted to metres. */
    widthMeters: number | null;
    heightMeters: number | null;
    areaSquareMeters: number | null;
    dxfUnit: string;
    polylineCount: number;
    labelCount: number;
    layerCount: number;
    visibleLayerCount: number;
    /** Layer names, visible first, capped at ASSISTANT_MAX_LAYER_NAMES. */
    layerNames: string[];
    layerNamesTruncated: boolean;
    /** Layer counts by classifier category, e.g. walls, equipment, text. */
    layersByCategory: Record<string, number>;
    facilityZoneCount: number;
    facilityZonesByType: Record<string, number>;
  } | null;
  /** Equipment overlays extracted from the drawing, by type and review state. */
  equipment: {
    total: number;
    byObjectType: Record<string, number>;
    byCategory: Record<string, number>;
    byProcessType: Record<string, number>;
    byReviewStatus: Record<string, number>;
  };
  layout: {
    trackLengthMeters: number;
    trackSections: number;
    junctions: number;
    stockers: number;
    equipment: number;
    ohts: number;
    totalComponents: number;
    /** Component rows with their quantities, e.g. "Overhead Straight Track": 42. */
    components: Record<string, number>;
    cost: {
      currency: string;
      estimatedTotal: number;
      pricedRows: number;
      unpricedRows: number;
      byCategory: Record<string, number>;
    };
  };
  validation: {
    hasRun: boolean;
    conflictCount: number;
    openEndpointCount: number;
    loops: number;
    railCount: number;
    closedTrack: boolean;
  };
  simulation: {
    source: 'legacy' | 'live';
    running: boolean;
    streamStatus: string;
    dispatchPolicy: string | null;
    simSpeed: number | null;
    simulationTimeS: number;
    reportStatus: 'running' | 'final' | 'none';
    kpis: Record<string, number>;
    throughput: {
      foupsPerHour: number | null;
      foupsPerDay: number | null;
      hopsPerHour: number | null;
      completedRequests: number | null;
    };
    ohts: {
      total: number;
      active: number;
      idle: number;
      roster: AssistantOhtRow[];
      rosterTruncated: boolean;
    };
  };
};

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function finiteRecord(source: Record<string, unknown> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!source) return out;
  for (const [key, value] of Object.entries(source)) {
    const parsed = num(value);
    if (parsed != null) out[key] = Number(parsed.toFixed(4));
  }
  return out;
}

function reportKpis(report: AmhsLiveReportPayload | null | undefined): Record<string, number> {
  if (!report) return {};
  const kpis = report.finalSimulationKpis ?? report.report?.simulation?.kpis;
  return finiteRecord(kpis as Record<string, unknown> | undefined);
}

function reportOhtRows(report: AmhsLiveReportPayload | null | undefined): AssistantOhtRow[] {
  const byOht = (report?.report?.optimization as { byOht?: unknown } | undefined)?.byOht;
  if (!Array.isArray(byOht)) return [];
  const rows: AssistantOhtRow[] = [];
  for (const raw of byOht) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const ohtId = typeof row.ohtId === 'string' ? row.ohtId : '';
    if (!ohtId) continue;
    const entry: AssistantOhtRow = { ohtId };
    if (typeof row.fleet === 'string' && row.fleet) entry.fleet = row.fleet;
    const requests = num(row.requestCount);
    if (requests != null) entry.requests = requests;
    const busy = num(row.busySec);
    if (busy != null) entry.busySec = Number(busy.toFixed(1));
    const util = num(row.utilization);
    if (util != null) entry.utilization = Number(util.toFixed(4));
    const path = num(row.pathLengthM);
    if (path != null) entry.pathLengthM = Number(path.toFixed(1));
    rows.push(entry);
  }
  return rows;
}

function countBy<T>(items: ReadonlyArray<T>, key: (item: T) => string | undefined | null) {
  const out: Record<string, number> = {};
  for (const item of items) {
    const value = key(item);
    const label = value && String(value).trim() ? String(value).trim() : 'unspecified';
    out[label] = (out[label] ?? 0) + 1;
  }
  return out;
}

function summarizeFloorPlan(
  plan: AssistantFloorPlanInput | undefined
): AssistantLayoutSummary['floorPlan'] {
  if (!plan || (!plan.name && !plan.floorPlanId && plan.polylineCount === 0)) return null;
  const scale = Number.isFinite(plan.metersPerWorldUnit) ? plan.metersPerWorldUnit : 0;
  const width = plan.bounds ? (plan.bounds.maxX - plan.bounds.minX) * scale : null;
  const height = plan.bounds ? (plan.bounds.maxY - plan.bounds.minY) * scale : null;
  const round1 = (value: number | null) =>
    value != null && Number.isFinite(value) ? Number(value.toFixed(1)) : null;
  const visibleLayers = plan.layers.filter((layer) => layer.visible !== false);
  const hiddenLayers = plan.layers.filter((layer) => layer.visible === false);
  const ordered = [...visibleLayers, ...hiddenLayers].map((layer) =>
    layer.visible === false ? `${layer.name} (hidden)` : layer.name
  );
  return {
    name: plan.name,
    loaded: true,
    visible: plan.visible,
    widthMeters: round1(width),
    heightMeters: round1(height),
    areaSquareMeters: width != null && height != null ? round1(width * height) : null,
    dxfUnit: plan.dxfUnit,
    polylineCount: plan.polylineCount,
    labelCount: plan.labelCount,
    layerCount: plan.layers.length,
    visibleLayerCount: visibleLayers.length,
    layerNames: ordered.slice(0, ASSISTANT_MAX_LAYER_NAMES),
    layerNamesTruncated: ordered.length > ASSISTANT_MAX_LAYER_NAMES,
    layersByCategory: countBy(plan.layers, (layer) => layer.category),
    facilityZoneCount: plan.regions.length,
    facilityZonesByType: countBy(plan.regions, (region) => region.type),
  };
}

function summarizeEquipment(
  equipment: AssistantEquipmentInput | undefined
): AssistantLayoutSummary['equipment'] {
  const instances = equipment?.instances ?? [];
  return {
    total: instances.length,
    byObjectType: countBy(instances, (item) => item.objectType),
    byCategory: countBy(instances, (item) => item.category),
    byProcessType: countBy(instances, (item) => item.equipmentType),
    byReviewStatus: countBy(instances, (item) => item.reviewStatus ?? 'potential'),
  };
}

export function buildAssistantLayoutSummary(input: {
  bom: LayoutBomResult;
  validation: ValidationState;
  simulation: AssistantSimulationInput;
  floorPlan?: AssistantFloorPlanInput;
  equipment?: AssistantEquipmentInput;
}): AssistantLayoutSummary {
  const { bom, validation, simulation } = input;

  const components: Record<string, number> = {};
  for (const row of bom.rows) {
    components[row.component] = (components[row.component] ?? 0) + row.quantity;
  }

  const report = simulation.live.liveReport ?? simulation.lastLiveReport ?? null;
  const kpis = reportKpis(report);
  const allRows = reportOhtRows(report);
  const roster = allRows.slice(0, ASSISTANT_MAX_OHT_ROWS);
  const active = allRows.filter((row) => (row.busySec ?? 0) > 0).length;
  const total = allRows.length > 0 ? allRows.length : (num(kpis.vehicleCount) ?? bom.summary.ohts);
  const runStatus = report?.report?.runStatus;

  return {
    floorPlan: summarizeFloorPlan(input.floorPlan),
    equipment: summarizeEquipment(input.equipment),
    layout: {
      trackLengthMeters: Number(bom.summary.trackLengthMeters.toFixed(2)),
      trackSections: bom.summary.trackSections,
      junctions: bom.summary.junctions,
      stockers: bom.summary.stockers,
      equipment: bom.summary.equipment,
      ohts: bom.summary.ohts,
      totalComponents: bom.summary.totalComponents,
      components,
      cost: {
        currency: bom.summary.currency,
        estimatedTotal: Math.round(bom.summary.estimatedTotalCost),
        pricedRows: bom.summary.pricedRows,
        unpricedRows: bom.summary.unpricedRows,
        byCategory: Object.fromEntries(
          Object.entries(bom.summary.costByCategory).map(([key, value]) => [
            key,
            Math.round(value ?? 0),
          ])
        ),
      },
    },
    validation: {
      hasRun: validation.hasRun,
      conflictCount: validation.conflicts.length,
      openEndpointCount: validation.openEndpoints.length,
      loops: validation.loops,
      railCount: validation.railCount,
      closedTrack: validation.closed,
    },
    simulation: {
      source: simulation.source,
      running: simulation.running,
      streamStatus: simulation.live.connectionStatus,
      dispatchPolicy: simulation.live.dispatchPolicy ?? null,
      simSpeed: num(simulation.live.simSpeed),
      simulationTimeS: Number(simulation.simulationTime.toFixed(1)),
      reportStatus: runStatus === 'running' || runStatus === 'final' ? runStatus : 'none',
      kpis,
      throughput: {
        foupsPerHour: num(kpis.foupThroughputPerHour),
        foupsPerDay: num(kpis.foupThroughputPerDay),
        hopsPerHour: num(kpis.hopThroughputPerHour),
        completedRequests: num(kpis.completedRequests),
      },
      ohts: {
        total,
        active,
        idle: Math.max(0, total - active),
        roster,
        rosterTruncated: allRows.length > roster.length,
      },
    },
  };
}
